import React, { useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { GameContextProvider } from '../../../resources/js/contexts/useGame';
import useViewModel from '../../hooks/useViewModel';
import PropTypes from 'prop-types';
import styles from './TableOfContents.module.scss';
import scrollElementIntoView from '../../../resources/js/helpers/scrollElementIntoView';
import EnhancedMarkdown from '../../../resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import Heading from '../../../resources/js/components/Heading/Heading';

function TableOfContents(props) {
  const viewModel = useViewModel(
    props,
    TableOfContents.viewModelBuilder.buildKey
  );

  if (!viewModel || viewModel.headings?.length === 0) return null;

  return (
    <nav aria-label={'Table of Contents'} className={styles.nav}>
      <Heading level={2} styleLevel={3} cssStyle={{ marginTop: '1.5em' }}>
        On this Page
      </Heading>
      <ul>
        {viewModel.headings.map((heading) => (
          <TableOfContentsHeading heading={heading} key={heading.key} />
        ))}
      </ul>
    </nav>
  );
}
TableOfContents.propTypes = {
  maximumHeadingLevel: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  minimumNumberOfHeadings: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
};

TableOfContents.viewModelBuilder = {
  buildKey: (props) =>
    `tableOfContents_${props.maximumHeadingLevel}_${props.maximumNumberOfHeadings}`,
  buildModel: (
    { maximumHeadingLevel, minimumNumberOfHeadings },
    { pageViewModels, headingComponents }
  ) => {
    const headingsTree = [];
    const headingsList = [];
    const latestHeadingOfEachLevel = {};

    headingComponents.forEach((component) => {
      let heading = component.type?.getHeading?.(component.props);

      if (/^h[1-6]$/.test(component.type?.name)) {
        const level = parseInt(component.type?.name.charAt(1));
        const text = renderHeaderText(component, pageViewModels.game);

        heading = {
          level,
          text,
          id: component.props.id,
        };
      }

      if (!heading || heading.level > maximumHeadingLevel) return;

      heading.key = heading.id;
      heading.subHeadings = [];

      const parentHeading = latestHeadingOfEachLevel[heading.level - 1];
      if (parentHeading) {
        parentHeading.subHeadings.push(heading);
      } else {
        headingsTree.push(heading);
      }
      headingsList.push(heading);

      latestHeadingOfEachLevel[heading.level] = heading;
    });

    if (
      minimumNumberOfHeadings &&
      headingsList.length < minimumNumberOfHeadings
    )
      return null;

    const ids = headingsList
      .map((x) => x.id)
      .filter((x, index, array) => array.indexOf(x) === index);
    ids.forEach((id) => {
      const headings = headingsList.filter((x) => x.id === id);
      if (headings.length > 1) {
        headings.forEach((heading, index) => {
          heading.id = null;
          heading.key += index.toString();
        });
      }
    });

    return { headings: headingsTree };
  },
};

function renderHeaderText(component, game) {
  return renderToStaticMarkup(
    <GameContextProvider game={game}>
      {component.props.children}
    </GameContextProvider>
  )
    .split('&lt;')
    .join('<')
    .split('&gt;')
    .join('>')
    .split('&#x27;')
    .join("'");
}

const TableOfContentsHeading = ({ heading }) => {
  const timeoutRef = useRef(null);

  const underlineHeading = (id) => {
    const element = document.getElementById(id);
    const underlineClassName = 'heading--underline';
    [...document.getElementsByClassName(underlineClassName)].forEach((x) =>
      x.classList.remove(underlineClassName)
    );
    element.classList.add(underlineClassName);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      element.classList.remove(underlineClassName);
    }, 1000);
  };

  return (
    <li>
      {heading.id ? (
        <a
          href={'#' + heading.id}
          onClick={(e) => {
            e.preventDefault();
            scrollElementIntoView('#' + heading.id);
            underlineHeading(heading.id);
          }}
        >
          <EnhancedMarkdown wrapped={false} markdown={heading.text} />
        </a>
      ) : (
        <EnhancedMarkdown wrapped={false} markdown={heading.text} />
      )}
      {heading.subHeadings.length > 0 && (
        <ul>
          {heading.subHeadings.map((subHeading) => (
            <TableOfContentsHeading heading={subHeading} key={subHeading.key} />
          ))}
        </ul>
      )}
    </li>
  );
};

TableOfContentsHeading.propTypes = {
  heading: PropTypes.shape({
    id: PropTypes.string,
    text: PropTypes.string.isRequired,
    subHeadings: PropTypes.array.isRequired,
  }),
};

export default TableOfContents;
